import { useCallback, useMemo, useState } from "react";
import { useTitanApp } from "@/hooks/useTitanApp";
import {
  getUsersByServiceID,
  getRoomsForAppointment,
  setAppointmentRooms,
} from "@/dispatchers/pharmacyServiceDispatchers";

interface ServiceTypeInfo {
  _id: string;
  name: string;
  serviceDuration: number;
  isRoomRequired: boolean;
}

export interface UsePatientBookingDataReturn {
  servicePharmacists: { id: string; name: string }[];
  serviceTypesInfo: ServiceTypeInfo[];
  serviceRooms: { id: string; name: string }[];
  serviceDurations: { value: string; label: string }[];
  isEventCalendarLoading: boolean;
  onServiceTypeSelected: (serviceId: string) => Promise<void>;
  companyId?: string;
  currentPharmacyId?: string;
  currentConsultationId?: string;
  availableDates: string[]; // yyyy-MM-dd
  getAvailableTimesForDate: (date: string) => string[]; // HH:mm
  handleGetAppointmentRooms: (b: any) => void;
}

const formatDurationLabel = (minutes: number) => {
  if (minutes < 60) return `${minutes} minutes`;
  const hours = minutes / 60;
  return hours % 1 === 0
    ? `${hours} hour${hours > 1 ? "s" : ""}`
    : `${hours} hours`;
};

export function usePatientBookingData(): UsePatientBookingDataReturn {
  const { appState, pharmacyServiceState, patientState, scheduleEventState } =
    useTitanApp();
  const { companyId, currentPharmacyId } = appState || {};
  const { usersAvailabilityLoader, consultationId } = patientState || {};
  const {
    serviceUsersByCompany = [],
    serviceRoomsByCompany = [],
    defaultPharmacyServices = [],
    appointmentRooms,
    isAppointmentRoomsLoading,
  } = pharmacyServiceState || {};
  const { isEventCalendarLoading, userAvailibility } = scheduleEventState || {};

  // Build availability map: date -> [HH:mm]
  const availabilityMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    try {
      const arr = Array.isArray(userAvailibility) ? userAvailibility : [];
      arr.forEach((entry: any) => {
        const freeSlots = (entry && entry.freeSlots) || {};
        Object.keys(freeSlots || {}).forEach((dayKey) => {
          const slots = Array.isArray(freeSlots[dayKey])
            ? freeSlots[dayKey]
            : [];
          slots.forEach((s: any) => {
            if (!s?.date || !s?.start) return;
            const d = new Date(String(s.date));
            if (isNaN(d.getTime())) return;
            const y = d.getUTCFullYear();
            const m = String(d.getUTCMonth() + 1).padStart(2, "0");
            const day = String(d.getUTCDate()).padStart(2, "0");
            const key = `${y}-${m}-${day}`;
            const time = String(s.start).slice(0, 5);
            if (!map[key]) map[key] = [];
            if (!map[key].includes(time)) map[key].push(time);
          });
        });
      });
      Object.keys(map).forEach((k) => map[k].sort());
    } catch {}
    return map;
  }, [userAvailibility]);
  const availableDates = useMemo(
    () => Object.keys(availabilityMap).sort(),
    [availabilityMap],
  );
  const getAvailableTimesForDate = useCallback(
    (date: string) => (availabilityMap[date] ? [...availabilityMap[date]] : []),
    [availabilityMap],
  );

  const [selectedServiceIdForRooms, setSelectedServiceIdForRooms] = useState<
    string | undefined
  >(undefined);

  // Map API users to simple {id, name}
  const servicePharmacists = useMemo(() => {
    if (!Array.isArray(serviceUsersByCompany)) return [] as any;
    return (serviceUsersByCompany as any[])
      .map((u) => {
        const id = u?.emailAddress || "";
        const name =
          u?.name ||
          [u?.firstName, u?.lastName].filter(Boolean).join(" ") ||
          u?.fullName ||
          "";
        if (!id || !name) return null;
        return { id: String(id), name: String(name) };
      })
      .filter(Boolean) as { id: string; name: string }[];
  }, [serviceUsersByCompany]);

  // Basic subset of service info needed by the modal
  const serviceTypesInfo: ServiceTypeInfo[] = useMemo(() => {
    if (!Array.isArray(defaultPharmacyServices)) return [] as any;
    return (defaultPharmacyServices as any[]).map((svc) => ({
      _id: String(svc?._id || ""),
      name: String(svc?.serviceName || ""),
      serviceDuration: Number(svc?.serviceDuration || 0),
      isRoomRequired: Boolean(svc?.isRoomRequired),
      serviceType: svc?.serviceType,
    }));
  }, [defaultPharmacyServices]);

  const serviceRooms = useMemo(() => {
    const rooms = appointmentRooms?.appointMentRooms;

    if (!Array.isArray(rooms)) return [];

    return rooms
      .map((room: any) => {
        const id = String(room?.roomId ?? "");
        const name = String(room?.roomName ?? "");
        return id && name ? { id, name } : null;
      })
      .filter(Boolean);
  }, [appointmentRooms]);

  // Duration options based on selected service
  const serviceDurations = useMemo(() => {
    if (!selectedServiceIdForRooms)
      return [] as { value: string; label: string }[];
    const svc = (defaultPharmacyServices as any[]).find(
      (s: any) => String(s?._id) === String(selectedServiceIdForRooms),
    );
    const base = parseInt(String((svc as any)?.serviceDuration ?? 0), 10) || 0;
    if (base <= 0) {
      const staticDurations = [15, 20, 30, 45, 60, 90, 120];
      return staticDurations.map((m) => ({
        value: String(m),
        label: formatDurationLabel(m),
      }));
    }
    const slots = Array.from({ length: 5 }, (_, i) => base * (i + 1));
    return slots.map((m) => ({
      value: String(m),
      label: formatDurationLabel(m),
    }));
  }, [selectedServiceIdForRooms, defaultPharmacyServices]);

  const onServiceTypeSelected = useCallback(
    async (serviceId: string) => {
      try {
        setSelectedServiceIdForRooms(serviceId);
        if (serviceId && companyId && currentPharmacyId) {
          await getUsersByServiceID(serviceId, companyId, currentPharmacyId);
        }
      } catch (e) {
        console.error(e);
      }
    },
    [companyId],
  );
  const handleGetAppointmentRooms = async (data: any) => {
    if (!data?.serviceType || !data?.date || !data?.time || !data?.duration) {
      setAppointmentRooms([]);
      return;
    }
    const payload = {
      serviceId: data.serviceType,
      pharmacyID: currentPharmacyId,
      appointmentDate: new Date(data.date).toISOString(),
      startTime: data.time,
      duration: Number(data.duration),
      IsNewAppointment: data.isNewAppointment !== undefined ? data.isNewAppointment : true,
    };

    await getRoomsForAppointment(payload);
  };

  return {
    servicePharmacists,
    serviceTypesInfo,
    serviceRooms,
    serviceDurations,
    isEventCalendarLoading,
    onServiceTypeSelected,
    companyId,
    currentPharmacyId,
    currentConsultationId : consultationId,
    availableDates,
    getAvailableTimesForDate,

    handleGetAppointmentRooms,
  };
}
